import { nullSafeToString } from '../lib/util';

/**
 * The shipment as the server has resolved it for a checkout. Read this after a save to confirm what
 * was actually persisted, rather than trusting local state.
 *
 * @property {?number} shippingServiceId - (Read only) The persisted shipping service id. Null when none is selected, or when a custom service was described instead.
 * @property {string} shippingServiceName - (Read only) The persisted shipping service name.
 * @property {string} shippingServiceNotes - (Read only) Free text describing the service. Carries the custom service description when one was given.
 * @property {boolean} isBlindShip - (Read only) Whether this shipment is blind shipped.
 * @property {boolean} isSaturdayDelivery - (Read only) Whether Saturday delivery was requested.
 * @property {boolean} isSignatureOnDelivery - (Read only) Whether signature on delivery was requested.
 * @property {string} referenceNumber1 - (Read only) The first shipping reference number.
 * @property {string} referenceNumber2 - (Read only) The second shipping reference number.
 * @property {?number} number - (Read only) The shipment number.
 * @property {string} trackingNumber - (Read only) The carrier tracking number, once available.
 * @property {Collaterate.Checkout~shipmentParty} shipTo - (Read only) The resolved destination.
 * @property {Collaterate.Checkout~shipmentParty} shipFrom - (Read only) The resolved origin.
 */

class CheckoutShipment {
    constructor (data = {}) {
        this.shippingServiceId = data.shippingServiceId || null;
        this.shippingServiceName = nullSafeToString(data.shippingServiceName);
        this.shippingServiceNotes = nullSafeToString(data.shippingServiceNotes);
        this.isBlindShip = Boolean(data.blindShip);
        this.isSaturdayDelivery = Boolean(data.saturdayDelivery);
        this.isSignatureOnDelivery = Boolean(data.signatureOnDelivery);
        this.referenceNumber1 = nullSafeToString(data.referenceNumber1);
        this.referenceNumber2 = nullSafeToString(data.referenceNumber2);
        this.number = data.number || null;
        this.trackingNumber = nullSafeToString(data.trackingNumber);

        this.shipTo = Object.freeze({
            name: nullSafeToString(data.shipToName),
            company: nullSafeToString(data.shipToCompany),
            phone: nullSafeToString(data.shipToPhone),
            address: nullSafeToString(data.shipToAddress),
            address2: nullSafeToString(data.shipToAddress2),
            city: nullSafeToString(data.shipToCity),
            stateName: nullSafeToString(data.shipToStateName),
            stateAbbreviation: nullSafeToString(data.shipToStateAbbreviation),
            postalCode: nullSafeToString(data.shipToZip),
            countryName: nullSafeToString(data.shipToCountryName),
            countryAbbreviation: nullSafeToString(data.shipToCountryAbbreviation),
        });

        this.shipFrom = Object.freeze({
            name: nullSafeToString(data.shipFromName),
            company: nullSafeToString(data.shipFromCompany),
            phone: nullSafeToString(data.shipFromPhone),
            address: nullSafeToString(data.shipFromAddress),
            address2: nullSafeToString(data.shipFromAddress2),
            city: nullSafeToString(data.shipFromCity),
            stateName: nullSafeToString(data.shipFromStateName),
            stateAbbreviation: nullSafeToString(data.shipFromStateAbbreviation),
            postalCode: nullSafeToString(data.shipFromZip),
            countryName: nullSafeToString(data.shipFromCountryName),
            countryAbbreviation: nullSafeToString(data.shipFromCountryAbbreviation),
        });

        Object.freeze(this);
    }
}

export default CheckoutShipment;

/**
 * One end of a shipment, as resolved by the server.
 *
 * @typedef {Object} Collaterate.Checkout~shipmentParty
 * @property {string} name - Contact name.
 * @property {string} company - Company name.
 * @property {string} phone - Contact phone.
 * @property {string} address - Street address.
 * @property {string} address2 - Second street address line.
 * @property {string} city - City.
 * @property {string} stateName - State name.
 * @property {string} stateAbbreviation - State abbreviation.
 * @property {string} postalCode - Postal code.
 * @property {string} countryName - Country name.
 * @property {string} countryAbbreviation - Country abbreviation.
 */
